import type { ReactNode } from 'react';

import { cn } from '../../utils';
import { productText } from '../report/styles';

/** Quiet drill-through/action treatment attached to a SectionHeader. */
export const SECTION_ACTION_CLASS =
  'focus-ring inline-flex min-h-8 shrink-0 items-center gap-1 rounded-md px-2 text-xs font-medium text-muted-foreground no-underline transition-colors hover:bg-surface-hover hover:text-foreground';

/**
 * SectionHeader — THE canonical in-content section heading: title + optional
 * quiet hint on ONE line. Section context never becomes a prose paragraph
 * between the heading and its content — the table/chart sits flush under this
 * header (max-design-rules rule 45: no explanatory body text; a methodology
 * detail rides the hint or a tooltip).
 */
export function SectionHeader({
  title,
  hint,
  size = 'default',
  action,
  className,
}: {
  title: string;
  hint?: ReactNode;
  /** `lg` for long-document surfaces (the playbook plan card) where sections
   *  need stronger separation than the default table-section weight. */
  size?: 'default' | 'lg';
  /** A local action for this section, aligned opposite its title. */
  action?: ReactNode;
  /** HORIZONTAL alignment only — an inset that lines the heading up with the
   *  text edge of the table beneath it (`DataTable` insets its rows by `px-3`,
   *  so a flush heading sits 12px to the left of its own columns). Never use it
   *  for vertical spacing: the rhythm between a heading and its content is the
   *  parent's `space-y-2`, and rule 60 owns it. */
  className?: string;
}) {
  return (
    <div className={cn('flex min-h-8 items-center justify-between gap-3', className)}>
      <div className="flex min-w-0 items-baseline gap-3">
        <h2 className={size === 'lg' ? productText.sectionTitleLg : productText.sectionTitle}>
          {title}
        </h2>
        {hint && <span className={productText.meta}>{hint}</span>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}
